import * as React from "react";
import { cn } from "@finster/ui";
import type { SkillDensity, SkillSummary } from "../types";

export interface SkillRowProps {
  skill: SkillSummary;
  density?: SkillDensity;
  disabled?: boolean;
  disabledReason?: string;
  id?: string;
  onSelect: (skill: SkillSummary) => void;
}

/**
 * One row in the Explore list. Rows open a Skill's detail rather than select
 * it, so this is a plain button: no option role, and no added-state cue. The
 * slug leads because that is the token the model consumes.
 */
export const SkillRow: React.FC<SkillRowProps> = ({
  skill,
  density = "default",
  disabled = false,
  disabledReason,
  id,
  onSelect,
}) => (
  <button
    id={id}
    type="button"
    aria-disabled={disabled || undefined}
    disabled={disabled}
    title={disabledReason || skill.description || skill.name}
    onClick={() => onSelect(skill)}
    className={cn(
      // justify-center so the two lines stay optically centred: min-h-16 is
      // taller than the content, and justify-start would pool the slack below.
      "flex w-full min-w-0 cursor-pointer flex-col items-start justify-center gap-1 rounded-lg border-0 text-left",
      "bg-transparent text-foreground hover:bg-background-tertiary",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus",
      density === "compact" ? "min-h-14 px-2 py-2" : "min-h-16 px-3 py-2.5",
      disabled && "cursor-not-allowed opacity-50"
    )}
  >
    <span className="w-full truncate text-sm font-medium">/{skill.slug}</span>
    {skill.description && (
      <span className="w-full truncate text-xs text-muted-foreground">{skill.description}</span>
    )}
  </button>
);
